import { Box, Button, Stack, Typography } from "@mui/material";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import { useTranslation } from "react-i18next";
import { useContact } from "../../hooks/useContact";
import { useCustomColors } from "../../hooks/useCustomColors";

const ForgotPasswordForm = () => {
  const { t } = useTranslation();
  const colors = useCustomColors();
  const { reviewUrl } = useContact();
  const steps = [
    {
      icon: SupportAgentRoundedIcon,
      title: t("forgot_step_contact_title"),
      body: t("forgot_step_contact_body"),
    },
    {
      icon: PersonSearchRoundedIcon,
      title: t("forgot_step_verify_title"),
      body: t("forgot_step_verify_body"),
    },
    {
      icon: KeyRoundedIcon,
      title: t("forgot_step_reset_title"),
      body: t("forgot_step_reset_body"),
    },
  ];

  return (
    <Stack sx={{ gap: "16px" }}>
      <Box
        sx={{
          p: "16px",
          borderRadius: "14px",
          border: `1px solid ${colors.border.accent}`,
          background: `linear-gradient(145deg, ${colors.background.field} 0%, ${colors.background.panelSecondary} 100%)`,
          boxShadow: `${colors.glow.cardEdge}, inset 0 0 28px ${colors.tint.primary}`,
        }}
      >
        <Stack direction="row" alignItems="center" sx={{ gap: "12px" }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.accent.gold,
              backgroundColor: colors.tint.gold,
              border: `1px solid ${colors.border.strong}`,
              boxShadow: `0 0 14px ${colors.tint.goldStrong}`,
            }}
          >
            <SecurityRoundedIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 800, color: colors.text.primary }}>
              {t("forgot_help_title")}
            </Typography>
            <Typography sx={{ mt: "3px", fontSize: "12.5px", lineHeight: 1.6, color: colors.text.secondary }}>
              {t("forgot_help_body")}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Stack
        component="ol"
        aria-label={t("forgot_steps_title")}
        sx={{ m: 0, p: 0, gap: "10px", listStyle: "none" }}
      >
        {steps.map(({ icon: Icon, title, body }, index) => (
          <Stack
            component="li"
            key={title}
            direction="row"
            alignItems="center"
            sx={{
              minHeight: "64px",
              p: "12px 14px",
              gap: "12px",
              borderRadius: "14px",
              backgroundColor: colors.background.field,
              border: `1px solid ${colors.border.accent}`,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 34,
                height: 34,
                flexShrink: 0,
                borderRadius: "11px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: colors.accent.gold,
                backgroundColor: colors.tint.gold,
              }}
            >
              <Icon sx={{ fontSize: "18px" }} />
              <Box
                component="span"
                sx={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  width: 17,
                  height: 17,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  fontSize: "9px",
                  fontWeight: 900,
                  color: colors.background.deep,
                  background: colors.gradient.goldPill,
                  border: `1px solid ${colors.accent.goldBright}`,
                }}
              >
                {index + 1}
              </Box>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 800, color: colors.text.primary }}>
                {title}
              </Typography>
              <Typography sx={{ mt: "2px", fontSize: "11.5px", lineHeight: 1.5, color: colors.text.secondary }}>
                {body}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>

      <Button
        component="a"
        href={reviewUrl ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        disabled={!reviewUrl}
        fullWidth
        sx={{ mt: "2px", height: "52px", borderRadius: "14px" }}
      >
        {t("forgot_contact_support")}
      </Button>

      <Typography sx={{ fontSize: "11.5px", lineHeight: 1.6, textAlign: "center", color: colors.text.disabled }}>
        {t("forgot_security_note")}
      </Typography>
    </Stack>
  );
};

export default ForgotPasswordForm;
